/* eslint-disable @typescript-eslint/no-explicit-any */
import { AddAnalyticsCustomAttribute } from 'utils/analytics/AnalyticsContext';
import { ENCODED_OBJECT_KEY, parseEncodedObject } from 'utils/urlUtils';
import { Head } from 'head/Head';
import { IOS_CONSUMER_APP_ID } from 'utils/deviceUtils';
import { IWithContextProps, consumer, withContext } from '@tock/coretypes';
import {
  IWithUseExperimentVariantProps,
  withUseExperimentVariant,
} from 'utils/withUseExperimentVariant';
import { Redirect, Route, RouteComponentProps, Switch, matchPath } from 'react-router-dom';
import { StaticContext } from 'react-router';
import { businessToData } from 'utils/analytics/amplitude';
import { first, forEach, includes, isEmpty, map, startsWith, toLower } from 'es-toolkit/compat';
import { getInMobileNative } from 'selectors/appSelector';
import { getTitleSuffix } from 'utils/seo';
import { iOSSmartAppBanner } from 'components/ios/IOSSmartAppBanner';
import { joinPath } from 'utils/pathUtils';
import { loadable } from 'utils/loadable';
import { logger } from '@tock/logging';
import { offeringSlugConsumerRoutePattern } from '@tock/shared/utils/offerings/routePattern';
import { parse } from '@tock/query-string';
import { reschedulePageOrder } from 'routes/protected/account/reschedule/RescheduleFlowRoute';
import BaseComponent from '@tock/shared/components/BaseComponent';
import BusinessProfileRoute from 'routes/business/BusinessProfileRoute';
import FlowRoute, { FlowRouteComponentProps } from 'components/render-prop-components/FlowRoute';
import OfferingDetailRoute from 'routes/business/OfferingDetailRoute';
import ProtectedRoute from 'routes/ProtectedRoute';
import SecondaryReceiptRoute from 'routes/business/SecondaryReceiptRoute';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

const ForgotPasswordRoute = loadable(() => import('routes/unprotected/ForgotPasswordRoute'));
const ResetPasswordRoute = loadable(() => import('routes/unprotected/ResetPasswordRoute'));
const SigninRoute = loadable(() => import('routes/unprotected/SigninRoute'));
const LogoutRoute = loadable(() => import('routes/protected/LogoutRoute'));
const CheckoutRouteEnhanced = loadable(() => import('routes/business/CheckoutRouteEnhanced'));
const RescheduleFlowRoute = loadable(
  () => import('routes/protected/account/reschedule/RescheduleFlowRoute')
);
const PostVisitFeedbackRoute = loadable(() => import('routes/business/PostVisitFeedbackRoute'));
const ReceiptRoute = loadable(() => import('routes/business/ReceiptRoute'));
const SyndicatedRoute = loadable(() => import('routes/business/syndicate-flow/SyndicatedRoute'));
const ContinuePurchaseRoute = loadable(
  () => import('routes/business/handsell-flow/ContinuePurchaseRoute')
);
const ContinueTransferRoute = loadable(
  () => import('routes/business/transfer-flow/ContinueTransferRoute')
);
const ContinueTableSideRoute = loadable(
  () => import('routes/business/tableside-flow/ContinueTableSideRoute')
);
const ConciergeCancelRoute = loadable(() => import('routes/business/ConciergeCancelRoute'));

const bestEffortDelay = { timeout: 1000 };

interface MatchParams {
  ticketTypeAccessToken?: string;
  widget?: string;
  businessDomain?: string;
}

type Props = RouteComponentProps<MatchParams> & IWithUseExperimentVariantProps;

interface ConnectProps extends DefaultConnectProps {
  reschedule: StoreType['reschedule'];
  lock: StoreType['lock'];
  calendar: StoreType['calendar'];
  inMobileNative: boolean;
}

class BusinessRoute extends BaseComponent<Props & ConnectProps & IWithContextProps> {
  navigationHasBeenSet = false;

  /**
   * This method sets the navigation fields in the store. It is factored as a method -- and
   * guarded with the navigationHasBeenSet class variable above -- so that routes that are
   * within BusinessRoute's render can call it in their componentDidMount method, which will
   * run before BusinessRoute's own componentDidMount method. This allows them to apply
   * additional settings beyond what BusinessRoute will apply by default.
   * @param isBusinessProfile whether the user is on the business profile page or not.
   */
  setNavigation = (isBusinessProfile: boolean) => {
    if (this.navigationHasBeenSet) {
      return;
    }
    const {
      actions: {
        navigation: { set },
      },
      location: { search },
      inMobileNative,
    } = this.props;
    const hasBack = inMobileNative || includes(search, 'tock');
    set({
      hasLogo: !hasBack,
      hasBack,
      isBusinessProfile,
    });
    this.navigationHasBeenSet = true;
  };

  businessProfileRouteRender = (routeProps: RouteComponentProps<any, StaticContext, unknown>) => (
    <BusinessProfileRoute {...routeProps} setNavigation={this.setNavigation} />
  );

  isTockKey(key: string): boolean {
    const lowerKey = toLower(key);
    return startsWith(lowerKey, 'utm_') || startsWith(lowerKey, 'tock_');
  }

  getEncodedObjectKeyValues(value: string): Dictionary<string> {
    const encodedObject = parseEncodedObject(value);
    return Object.keys(encodedObject)
      .filter(this.isTockKey)
      .reduce(
        (accumulator, currentKey) => ({
          ...accumulator,
          [currentKey]: encodedObject[currentKey],
        }),
        {}
      );
  }

  componentDidMount() {
    const {
      actions: {
        calendar: { clearCalendarAndOfferings },
        lastGuest: { setLastGuest },
        purchase: { setKeyValues },
        queryParam: { setQuery },
      },
      app: { config, fastRoutingBetweenContext },
      calendar: { calendar, businessId: calendarBusinessId },
      location: { pathname, search },
      replaceRouteWith,
    } = this.props;
    const business = config?.business;
    const isWidget = includes(pathname, '/widget');
    window.RunningInWidget = isWidget;
    if (isWidget) {
      document.body.classList.add('TockWidgetBody');
    }

    // TODO: We previously checked consent state here and set a consent cookie, do we need to replicate this?

    // In the case that there is a BusinessProfileRoute inner render,
    // this call may have no effect because it's already been run
    // by BusinessProfileRoute.componentDidMount().
    this.setNavigation(false);

    const query = parse(search);
    let allowedParams: Dictionary<string> = {};
    let keyValues: Dictionary<string> = {};

    if (query.syndicate_key_value) {
      try {
        const syndicateKeyValues = JSON.parse(query.syndicate_key_value);
        keyValues = syndicateKeyValues;
      } catch (e) {
        logger.debug(e as string);
      }
    }
    forEach(query, (value: string, fullKey: string) => {
      const key = toLower(fullKey);
      if (this.isTockKey(key)) {
        keyValues[key] = value;
        allowedParams[key] = value;
      }
      if (key === 'gclid' || key === '_ga') {
        allowedParams[key] = value;
      }
      if (key === ENCODED_OBJECT_KEY) {
        const encodedObject = this.getEncodedObjectKeyValues(value);
        keyValues = {
          ...keyValues,
          ...encodedObject,
        };
        allowedParams = {
          ...allowedParams,
          ...encodedObject,
        };
      }
      if (query.utm_source === 'google-dining' && key === 'booking_id') {
        keyValues.google_booking_id = value;
      }

      if (query.email || query.first_name || query.last_name || query.telephone || query.phone) {
        const guest = {
          email: query.email,
          firstName: query.first_name || query.firstName,
          lastName: query.last_name || query.lastName,
          phone: query.telephone || query.phone,
        };
        setLastGuest(guest);
      }
    });

    if (!startsWith(pathname, '/oauth-check') && !isEmpty(allowedParams)) {
      setQuery(allowedParams);
    }

    // Unless this is a redirect from social login or if it is not empty, set the state.
    if (!startsWith(pathname, '/oauth-check') && !isEmpty(keyValues)) {
      setKeyValues(keyValues);
    }

    // Check if calendar was not server side rendered. Otherwise it is usually a bot
    if (fastRoutingBetweenContext) {
      clearCalendarAndOfferings();
      this.handleFetchOfferings(config);
      this.handleFetchCalendar(config);
    } else if (!calendar) {
      requestIdleCallback(() => this.handleFetchOfferings(config), bestEffortDelay);
      requestIdleCallback(() => this.handleFetchCalendar(config), bestEffortDelay);
    } else if ((calendarBusinessId?.toString() ?? '') !== (business?.id?.toString() ?? '')) {
      // Navigating to a business specific page with history.push instead of an
      // href won't clear out the calendar store or trigger ssr
      // Prefer an href
      // eslint-disable-next-line no-console
      console.warn('Navigated to a business page with mismatching calendar info');
      requestIdleCallback(() => this.handleFetchOfferings(config), bestEffortDelay);
      requestIdleCallback(() => this.handleFetchCalendar(config), bestEffortDelay);
    }

    const domainName = business?.domainName;
    if (
      query.utm_source === 'search' &&
      includes(document.referrer, '.google.') &&
      (pathname === `/${domainName}` || pathname === `/${domainName}/`)
    ) {
      replaceRouteWith(`/${domainName}/search`);
    }
  }

  componentWillUnmount() {
    if (BROWSER) {
      window.RunningInWidget = false;
    }
  }

  handleFetchCalendar(config: consumer.ConsumerConfig | undefined) {
    const business = config?.business;
    const domain = config?.domain;
    const businessId = business?.id;
    if (domain?.isOpen || !businessId) {
      return null;
    }
    const {
      actions: {
        calendar: { fetchV2, fetchPreview },
      },
      location: { pathname, search },
      match: { url },
    } = this.props;
    const { previewMode, endDate } = parse(search);
    if (previewMode) {
      return fetchPreview(endDate);
    }
    // Match private experiences/events
    const accessTokenMatch = matchPath<MatchParams>(pathname, {
      path: [
        joinPath(url, `/experience/private/:ticketTypeAccessToken`),
        joinPath(url, `/event/private/:ticketTypeAccessToken`),
        joinPath(url, `/detail/private/:ticketTypeAccessToken`),
      ],
    });
    const accessToken = accessTokenMatch?.params?.ticketTypeAccessToken;
    return fetchV2(accessToken, false, businessId);
  }

  handleFetchOfferings(config: consumer.ConsumerConfig | undefined) {
    const business = config?.business;
    const domain = config?.domain;
    const businessId = business?.id;
    if (domain?.isOpen || !businessId) {
      return null;
    }
    const {
      actions: {
        calendar: { fetchOfferings, fetchOfferingsPreview },
      },
      location: { pathname, search },
      match: { url },
    } = this.props;
    const { previewMode, endDate } = parse(search);
    if (previewMode) {
      return fetchOfferingsPreview(endDate);
    }
    const accessTokenMatch = matchPath<MatchParams>(pathname, {
      path: joinPath(
        url,
        `/${offeringSlugConsumerRoutePattern('offering', [
          'experience',
          'event',
          'special-table',
          'detail',
        ])}/private/:ticketTypeAccessToken`
      ),
    });
    const accessToken = accessTokenMatch?.params.ticketTypeAccessToken;
    if (accessToken) {
      return fetchOfferings(accessToken);
    }
    return fetchOfferings(undefined, undefined, businessId);
  }

  renderRescheduleFlowRoute(
    routeProps: RouteComponentProps,
    flowRouteProps: FlowRouteComponentProps
  ) {
    return <RescheduleFlowRoute {...routeProps} {...flowRouteProps} />;
  }

  getAnalyticsData() {
    const { app } = this.props;

    return businessToData(app.config?.business);
  }

  render() {
    const {
      app,
      app: { config },
      match: {
        url: matchUrl,
        params: { widget },
      },
      calendar,
      experimentVariants,
    } = this.props;

    const business = config?.business;

    if (!business) {
      return null;
    }
    const { profileImage, name: businessName, twitterHandle } = business;

    const newImages = map(profileImage ?? [], (im) => im.imageUrl);
    const attributeImage = first(newImages) ?? undefined;

    const title = `${businessName}${getTitleSuffix(business)}`;
    const description = business.description ?? `Reservations at ${businessName} powered by Tock.`;
    const keywords = `prix fixe, tickets, booking, reservation, restaurant, dining, dinner reservations, tasting menu, fine dining, tock, experience, deposits, book your experience, ${businessName}, ${businessName} reservations, ${businessName} restaurant reservations, ${businessName} to go, ${businessName} delivery, ${businessName} pickup, ${businessName} ordering, managed by tock`;
    const image = attributeImage;
    const type = 'restaurant';
    const offeringExclusivity = calendar?.experienceDetail?.offering?.exclusivity;

    const enableSmartAppBanner = experimentVariants?.get('IOSSmartAppBannerBusinessPage') === 1;

    // The app expects `/t/{domain}` URLs for business pages. It does not
    // support `/{domain}` URLs directly as those can be confused with other
    // as yet unsupported Tock routes.
    const smartAppBanner = enableSmartAppBanner
      ? iOSSmartAppBanner(
          IOS_CONSUMER_APP_ID,
          `https://www.exploretock.com/t/${business.domainName}?tock_source=tock&tock_medium=ios_smart_app_banner`
        )
      : null;

    return (
      <AddAnalyticsCustomAttribute value={this.getAnalyticsData()}>
        <Head
          title={title}
          description={description}
          keywords={keywords}
          image={image}
          site={twitterHandle ?? ''}
          siteName={businessName ?? ''}
          type={type}
        >
          {smartAppBanner}
        </Head>
        {widget ? (
          <Switch>
            {/* this.businessProfileRouteRender wraps the BusinessProfileRoute component */}
            <Route render={this.businessProfileRouteRender} />
          </Switch>
        ) : (
          <Switch>
            <Redirect from={`/:businessDomain/forgot`} to={`/:businessDomain/forgot-password`} />
            <Redirect from={`/:businessDomain/signin`} to={`/:businessDomain/signup`} />
            <Route
              path={`/:businessDomain/forgot-password`}
              render={(routeProps: RouteComponentProps) => (
                <ForgotPasswordRoute {...routeProps} baseRoute={matchUrl} />
              )}
            />
            <Route path={`/:businessDomain/reset`} component={ResetPasswordRoute} />
            <Route path={`/:businessDomain/:loginOrSignup(login|signup)`} component={SigninRoute} />
            <Route
              path={`/:businessDomain/concierge/cancel-verify-email`}
              component={ConciergeCancelRoute}
            />
            <ProtectedRoute app={app} path={`/:businessDomain/logout`} component={LogoutRoute} />
            {/* Receipt is a goal path in GA, we avoid using it for anything that is not checkout
            related */}
            <Route
              path={`/:businessDomain/${offeringSlugConsumerRoutePattern('offering', [
                'event',
                'special-table',
                'detail',
              ])}/private/:ticketTypeAccessToken`}
              component={OfferingDetailRoute}
            />
            <Route
              path={`/:businessDomain/${offeringSlugConsumerRoutePattern('offering', [
                'event',
                'special-table',
                'detail',
              ])}/:experienceId`}
              render={(routeProps: RouteComponentProps<any>) => {
                const inTockNonExclusive = offeringExclusivity === 'NONE';
                const notExclusiveOffering =
                  offeringExclusivity !== 'RESERVE' && offeringExclusivity !== 'SAPPHIRE';
                if (inTockNonExclusive || notExclusiveOffering) {
                  return <OfferingDetailRoute {...routeProps}></OfferingDetailRoute>;
                } else {
                  return (
                    <Redirect
                      to={{
                        ...routeProps.location,
                        pathname: `/${business.domainName}`,
                      }}
                    />
                  );
                }
              }}
            />
            {/* <Route
              path={`/:businessDomain/reservations/:purchaseId/survey`}
              component={ReservationSurveyRoute}
            /> */}
            <Route path={'/:businessDomain/continue-purchase'} component={ContinuePurchaseRoute} />
            <Route path={'/:businessDomain/complete-transfer'} component={ContinueTransferRoute} />
            <ProtectedRoute
              app={app}
              path={'/:businessDomain/tableside'}
              component={ContinueTableSideRoute}
            />
            <Route
              path={`/:businessDomain/reservations/:purchaseId/feedback`}
              component={PostVisitFeedbackRoute}
            />
            <Route path={`/:businessDomain/reservations/:purchaseId`} component={ReceiptRoute} />
            <Route
              path={`/:businessDomain/reservations/:purchaseId/syndicate`}
              component={SyndicatedRoute}
            />
            <Route path={`/:businessDomain/receipt/syndicate`} component={SyndicatedRoute} />
            <Route path={`/:businessDomain/receipt/secondary`} component={SecondaryReceiptRoute} />
            <Route path={`/:businessDomain/receipt`} component={ReceiptRoute} />
            <Route
              path={`/:businessDomain/survey`}
              render={(routeProps) => {
                return (
                  <Redirect
                    to={{
                      ...routeProps.location,
                      pathname: `/${routeProps.match.params.businessDomain}/receipt/survey`,
                    }}
                  />
                );
              }}
            />
            <Route path={`/:businessDomain/checkout`} component={CheckoutRouteEnhanced} />
            <FlowRoute<Props>
              {...this.props}
              isInitialized
              path={`/:businessDomain/reschedule`}
              pageOrder={reschedulePageOrder}
              render={this.renderRescheduleFlowRoute}
            />
            <Route path={`/:businessDomain/syndicate`} component={SyndicatedRoute} />
            {/* this.businessProfileRouteRender wraps the BusinessProfileRoute component */}
            <Route render={this.businessProfileRouteRender} />
          </Switch>
        )}
      </AddAnalyticsCustomAttribute>
    );
  }
}

const mapStateToProps = (state: StoreType) => {
  return {
    reschedule: state.reschedule,
    calendar: state.calendar,
    inMobileNative: getInMobileNative(state),
    lock: state.lock,
    config: state.app.config,
  };
};

export default connect<Props & IWithUseExperimentVariantProps, ConnectProps>(
  withContext(withUseExperimentVariant('IOSSmartAppBannerBusinessPage', BusinessRoute)),
  mapStateToProps
);
